import { BookmarkModel } from '../models/bookmark.model';
import { BookmarkEntryModel } from '../models/bookmark.entry.model';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { BookmarkSortModel } from '../models/bookmark-sort.model';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { ContentType } from 'src/app/story/models/content-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { StoryModel } from 'src/app/story/models/story.model';
import { BookmarkGroupModel } from '../models/bookmark.group.model';
import { UserAppModel } from 'src/app/auth/models/user-app.model';
import { defaultLocale } from 'src/app/locale-config.module';
import { BookmarkViewModeTag } from '../models/bookmark-view-mode.enum';

export class LoadBookmarks {
  static readonly type = '[Bookmarks] Load';
  constructor(public query: string) {}
}

export class LoadOrganizationBookmarks {
  static readonly type = '[Bookmarks] Load Organization Bookmarks';
  constructor(public organizationId: number) {}
}

export class LoadCollectionBookmarks {
  static readonly type = '[Bookmarks] Load Collection Bookmarks';
  constructor(public organizationId: number, public collectionId: number) {}
}

export class SelectBookmark {
  static readonly type = '[Bookmarks] Select Bookmark';
  constructor(public id: string, public type: ContentType, public preview = false) {}
}

export class SelectBookmarkSuccess {
  static readonly type = '[Bookmarks] Select Bookmark Success';
  constructor(public bookmark: BookmarkModel) {}
}

export class LoadBookmarksSuccess {
  static readonly type = '[Bookmarks] Load Success';
  constructor(public result: BookmarkModel[]) {}
}

export class DeleteBookmark {
  static readonly type = '[Bookmarks] Delete Bookmark';
  constructor(public bookmarkId: string) {}
}

export class DeleteBookmarkSuccess {
  static readonly type = '[Bookmarks] Bookmark Delete Success';
  constructor(public bookmarkId: string) {}
}

export class DeleteBookmarkFail {
  static readonly type = '[Bookmarks] Bookmark Delete Fail';
  constructor(public bookmarkId: string, public message: string) {}
}

export class SaveLocalBookmark {
  static readonly type = '[Bookmarks] Save Local Bookmark';
  constructor(
    public bookmarkEntry: BookmarkEntryModel,
    public inplace: boolean,
    public includeIndex: boolean = true,
  ) {}
}
export class SaveBookmark {
  static readonly type = '[Bookmarks] Save Bookmark';
  constructor(
    public bookmarkEntry: BookmarkEntryModel,
    public inplace: boolean,
    public includeIndex: boolean = true,
  ) {}
}

export class SaveBookmarkSuccess {
  static readonly type = '[Bookmarks] Bookmark Saved';
  constructor(
    public bookmark: BookmarkModel,
    public story: StoryModel,
    public method: 'save' | 'update',
  ) {}
}

export class SaveBookmarkFail {
  static readonly type = '[Bookmarks] Bookmark Save Failed';
  constructor(public bookmarkEntry: BookmarkEntryModel) {}
}

export class CancelSaveBookmark {
  static readonly type = '[Bookmarks] Cancel Save Bookmark';
  constructor() {}
}

export class SaveBookmarkThumbnails {
  static readonly type = '[Bookmarks] Save Bookmark Thumbnails';
  constructor(
    public method: string,
    public bookmarkId: string,
    public bookmarkEntry: BookmarkEntryModel,
    public thumbnails: Map<string, StoryChapterThumbnail>,
    public chapterIdMap?: { [id: string]: string },
  ) {}
}

export class ResetBookmarks {
  static readonly type = '[Bookmarks] Reset';
  constructor() {}
}

export class ClearBookmarks {
  static readonly type = '[Bookmarks] Clear Bookmarks';
  constructor() {}
}

export class SortBookmarks {
  static readonly type = '[Bookmarks] Sort';
  constructor(public sort: BookmarkSortModel) {}
}

export class CheckBookmark {
  static readonly type = '[Bookmarks] Check Bookmark';
  constructor(public id: string, public checked: boolean) {}
}

export class SetCheckedBookmarks {
  static readonly type = '[Bookmarks] Set Checked Bookmarks';
  constructor(public ids: string[]) {}
}

export class ExportBookmarks {
  static readonly type = '[Bookmarks] Export Bookmarks';
  constructor(
    public bookmarks: BookmarkModel[],
    public organizationId: number,
    public collections: CollectionModel[],
    public widgetURL: string,
    public userLanguage: string,
    public userApp: UserAppModel,
    public widgetOptions?: WidgetOptions,
    public offlineEnabled?: boolean,
    public productAccountId?: string,
  ) {}
}

export class ExportBookmarksSuccess {
  static readonly type = '[Bookmarks] Export Bookmarks Success';
  constructor(public bookmarks: BookmarkModel[]) {}
}

export class ExportBookmarksError {
  static readonly type = '[Bookmarks] Export Bookmarks Error';
  constructor(public bookmarks: BookmarkModel[]) {}
}

export class UpdateBookmarkCollections {
  static readonly type = '[Bookmarks] Update Bookmark Collections';
  constructor(
    public bookmark: BookmarkModel,
    public addCollections: CollectionModel[] = [],
    public removeCollections: Map<CollectionModel, number> = new Map(),
  ) {}
}

export class UpdateBookmarkCollectionsSuccess {
  static readonly type = '[Bookmarks] Update Bookmark Collections Success';
  constructor(
    public bookmark: BookmarkModel,
    public addCollections: CollectionModel[],
    public removeCollections: CollectionModel[],
  ) {}
}

export class UpdateBookmarkCollectionsError {
  static readonly type = '[Bookmarks] Update Bookmark Collections Error';
  constructor(
    public bookmark: BookmarkModel,
    public addCollections: CollectionModel[],
    public removeCollections: CollectionModel[],
  ) {}
}

export class AddBookmarkCollectionsSuccess {
  static readonly type = '[Bookmarks] Add Bookmark Collections Success';
  constructor(public bookmark: BookmarkModel, public collections: CollectionModel[]) {}
}

export class AddBookmarkCollectionsError {
  static readonly type = '[Bookmarks] Add Bookmark Collections Error';
  constructor(public bookmark: BookmarkModel, public collections: CollectionModel[]) {}
}

export class RemoveBookmarkCollectionsSuccess {
  static readonly type = '[Bookmarks] Remove Bookmark Collections Success';
  constructor(public bookmark: BookmarkModel, public collections: CollectionModel[]) {}
}

export class RemoveBookmarkCollectionsError {
  static readonly type = '[Bookmarks] Remove Bookmark Collections Error';
  constructor(public bookmark: BookmarkModel, public collections: CollectionModel[]) {}
}

export class CopyBookmark {
  static readonly type = '[Bookmarks] Copy Bookmark';
  constructor(
    public id: string,
    public title: string,
    public type: ContentType,
    public lang = defaultLocale.lang,
    public groupAccess?: BookmarkGroupModel,
    public collections?: CollectionModel[],
  ) {}
}

export class CopyBookmarkSuccess {
  static readonly type = '[Bookmarks] Copy Bookmark Success';
  constructor(public newBookmark: BookmarkModel) {}
}

export class CopyBookmarkFailure {
  static readonly type = '[Bookmarks] Copy Bookmark Failure';
  constructor(public id: string, public error: HttpErrorResponse) {}
}

export class CancelCopyBookmark {
  static readonly type = '[Bookmarks] Cancel Copy Bookmark';
  constructor() {}
}

export class SetBookmarkText {
  static readonly type = '[Bookmarks] Set Bookmark Text';
  constructor(public title: string) {}
}

export class SetBookmarkViewMode {
  static readonly type = '[Bookmarks] Set Bookmark View Mode';
  constructor(public viewMode: BookmarkViewModeTag) {}
}

export class SetSelectedCategory {
  static readonly type = '[Bookmarks] Set Selected Category';
  constructor(public category: 'individual' | 'organization' | 'collection') {}
}
